import React from 'react';
import {
	type AutoListCommonExtendProps,
	AutoListField,
} from '@netx/core-components/AutoListField';
import {useAutoList} from '@netx/core-next/hooks/autoList/useAutoList';
import {i18n} from '@netx/core-next/locales/i18n-instance';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {RecognitionFaceAvatar} from '@netx/core-recognition-face/components/RecognitionFaceAvatar';
import {styled} from '@netx/core-components/Theme';
import {useSharedRef} from '@netx/core-components/hooks/useSharedRef';
import {getPeople} from '../../api';
import {PersonStatus, type IPerson} from '../../schema';
import {PersonListItem} from '../PersonListItem';

export {type IPerson} from '../../schema';

const getNewOption = (inputValue: string): IPersonOption => ({
	id: 'new',
	name: inputValue,
	assetCount: undefined,
	avatarUrl: '',
	status: PersonStatus.IDENTIFIED,
	creationDate: new Date().toISOString(),
	modificationDate: new Date().toISOString(),
});

export type IPersonOption = Pick<
	IPerson,
	'id' | 'name' | 'avatarUrl' | 'status' | 'creationDate' | 'modificationDate'
> & {
	assetCount?: number;
};

export type PersonAutoListRef = React.ForwardedRef<HTMLDivElement>;

const DEFAULT_NAME = 'person-auto-list';

const _getPeople = async ({
	query,
	params,
}: {
	query: string;
	params?: Record<string, any>;
}) => {
	return (
		await getPeople({
			filters: {
				name: query,
				status: PersonStatus.IDENTIFIED,
			},
		})
	).results as IPersonOption[];
};

export interface PersonAutoListProps<
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
> extends AutoListCommonExtendProps<
	IPersonOption,
	Multiple,
	DisableClearable,
	true
> {
	avatarUrl?: string;
	isNew?: boolean;
}

/**
 * Default get option label fn
 * @param option Option
 */
const defaultGetOptionLabel = (option?: IPersonOption | string) => {
	// console.log('option', option);
	if (typeof option === 'string') return option;
	if (!option) return '';
	if (option.status === PersonStatus.UNIDENTIFIED)
		return i18n.t('i18n.unidentified');
	return option.name || '';
};

/**
 * Default get option selected fn
 * @param option Option
 * @param value Value
 */
const defaultGetOptionSelected = (
	option: IPersonOption,
	value?: IPersonOption,
) =>
	Array.isArray(value)
		? value.some((v) => v.id === option.id)
		: option.id === value?.id;

/** should option be disabled */
const defaultGetOptionDisabled = (option: IPersonOption) => false; //option.id === '';

const PersonAutoListRoot = styled('div')(({theme}) => ({
	display: 'flex',
	alignItems: 'flex-start',
	flex: '1 1 auto',
	gap: theme.spacing(1),
}));

const PARAMS_STUB = {};

export const PersonAutoListPrimitive = <Multiple extends boolean = false>(
	{
		avatarUrl,
		isNew,
		filterOptions,
		getOptionLabel = defaultGetOptionLabel,
		getOptionSelected = defaultGetOptionSelected,
		getOptionDisabled = defaultGetOptionDisabled,
		onClose,
		onInputChange,
		onOpen,
		...props
	}: PersonAutoListProps<Multiple, true>,
	ref: PersonAutoListRef,
) => {
	const inputRef = useSharedRef<HTMLInputElement>(null, [props.inputRef]);

	const {loadingOptions, query, queryStr, setQueryStr, setIsOpen} = useAutoList(
		{
			params: PARAMS_STUB,
			isQueryValid: (query) => true,
			filterOptions,
			queryFn: _getPeople,
		},
	);

	const placeholder =
		props.multiple !== true ? i18n.t('i18n.selectPerson') : undefined;

	return (
		<PersonAutoListRoot>
			<RecognitionFaceAvatar
				onClick={() => {
					inputRef.current?.focus();
				}}
				aria-hidden="true"
				avatarUrl={avatarUrl}
			/>
			<AutoListField<IPersonOption, Multiple, true, true>
				ref={ref}
				disableClearable
				hasSecondaryText
				blurOnSelect={false}
				label={i18n.t('i18n.addPerson')}
				loadingOptions={loadingOptions}
				multiple={false as Multiple}
				name={DEFAULT_NAME}
				placeholder={placeholder}
				isOptionEqualToValue={getOptionSelected}
				renderOption={(props, option, state) => (
					<PersonListItem {...props} person={option} />
				)}
				inputValue={queryStr}
				{...props}
				inputRef={inputRef}
				filterOptions={(options, params) => {
					const _options = filterOptions
						? filterOptions([...options])
						: [...options];
					if (props.freeSolo && params.inputValue !== '') {
						_options.unshift(getNewOption(params.inputValue));
					}
					return _options;
				}}
				options={query.data ?? []}
				getOptionDisabled={getOptionDisabled}
				getOptionLabel={getOptionLabel}
				onClose={splitEventHandler(onClose, () => setIsOpen(false))}
				onOpen={splitEventHandler(onOpen, () => setIsOpen(true))}
				onInputChange={splitEventHandler(
					onInputChange,
					(event, value, reason) => setQueryStr(value),
				)}
			/>
		</PersonAutoListRoot>
	);
};

/**
 * User auto list field
 */
export const PersonAutoList = React.forwardRef(PersonAutoListPrimitive) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	props: PersonAutoListProps<Multiple, DisableClearable>,
) => JSX.Element;

export default PersonAutoList;
